/**
 * AIAssistantTeaser Component
 * Teaser section for AI assistant features
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";
import { tokenStorage } from "../../config/api";
import { useGetProfileQuery } from "../../store/api/authApi";
import Icon from "../AppIcon";
import Button from "../ui/Button";

const AIAssistantTeaser: React.FC = () => {
	const navigate = useNavigate();
	const accessToken = tokenStorage.getAccessToken();
	// Only fetch profile if we have a token (skip query if no token)
	const { data: user } = useGetProfileQuery(
		accessToken ? undefined : skipToken,
	);

	const handleLearnMore = () => {
		if (user) {
			// User is authenticated, go to AI assistant
			navigate("/ai-assistant");
		} else {
			// User not authenticated, redirect to login
			navigate("/login?redirect=/ai-assistant");
		}
	};

	return (
		<section className="py-16 bg-gradient-to-br from-primary/5 to-accent/5">
			<div className="container mx-auto px-4 sm:px-6 lg:px-8">
				<div className="max-w-4xl mx-auto text-center">
					{/* Icon */}
					<div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 text-primary rounded-full mb-6">
						<Icon name="Sparkles" size={40} />
					</div>

					{/* Title */}
					<h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
						Need Help Deciding?
					</h2>

					{/* Description */}
					<p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
						Our AI assistant can explain property pros and cons, help you
						compare options, and answer your questions about any listing. Get
						personalized insights to make the best decision for your next home.
					</p>

					{/* Features Grid */}
					<div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
						<div className="bg-card border border-border rounded-lg p-6">
							<div className="w-12 h-12 bg-success/10 text-success rounded-full flex items-center justify-center mx-auto mb-4">
								<Icon name="MessageSquare" size={24} />
							</div>
							<h3 className="font-semibold text-foreground mb-2">
								Ask Questions
							</h3>
							<p className="text-sm text-muted-foreground">
								Get instant answers about properties, neighborhoods, and more
							</p>
						</div>

						<div className="bg-card border border-border rounded-lg p-6">
							<div className="w-12 h-12 bg-primary/10 text-primary rounded-full flex items-center justify-center mx-auto mb-4">
								<Icon name="BarChart" size={24} />
							</div>
							<h3 className="font-semibold text-foreground mb-2">
								Compare Properties
							</h3>
							<p className="text-sm text-muted-foreground">
								Side-by-side comparisons with AI-powered insights
							</p>
						</div>

						<div className="bg-card border border-border rounded-lg p-6">
							<div className="w-12 h-12 bg-accent/10 text-accent rounded-full flex items-center justify-center mx-auto mb-4">
								<Icon name="Lightbulb" size={24} />
							</div>
							<h3 className="font-semibold text-foreground mb-2">
								Get Recommendations
							</h3>
							<p className="text-sm text-muted-foreground">
								Personalized suggestions based on your preferences
							</p>
						</div>
					</div>

					{/* CTA Button */}
					<Button size="lg" variant="outline" onClick={handleLearnMore}>
						<Icon name="Sparkles" size={20} className="mr-2" />
						{user ? "Try AI Assistant" : "Sign In to Use AI Assistant"}
					</Button>
				</div>
			</div>
		</section>
	);
};

export default AIAssistantTeaser;
